import { api } from '../api/client'
import type { Commodity, CommodityQuery } from '../api/client'
import { useHasPermission } from '../api/session'
import { useListParams } from '../hooks/useListParams'
import { useListQuery } from '../hooks/useListQuery'
import { Card } from '../components/card/Card'
import { Button } from '../components/common/Button'
import { MultiSelect } from '../components/common/MultiSelect'
import type { MultiSelectOption } from '../components/common/MultiSelect'
import { EmptyState, ErrorBox } from '../components/common/Feedback'
import { FilterBar, FilterEnd, FilterFixed } from '../components/common/FilterBar'
import { PermissionNotice } from '../components/common/PermissionNotice'
import { DataTable } from '../components/table/DataTable'
import type { Column } from '../components/table/types'

/** Das Recht, das diese Ansicht braucht. */
const PERMISSION = 'commodity:read'

//: Alle elf passen auf eine Seite. Geblättert wird hier nie — die Angabe
//: steht trotzdem, weil der Endpunkt sie erwartet.
const PAGE_SIZE = 50

/** Der Wert aus der Datenbank, in Worten.
 *
 * Die Zuordnung steht HIER und nicht im Backend: `ENERGY` ist der
 * Speicherwert, "Energie" ist Deutsch und damit Sache der Anzeige. Die
 * Schnittstelle bedient auch den MCP-Server, und der soll keine deutschen
 * Bezeichnungen ausliefern.
 *
 * Was hier fehlt, wird zum Rohwert selbst — sichtbar statt verschwiegen.
 */
const CATEGORIES: Record<string, string> = {
  ENERGY: 'Energie',
  METAL: 'Metall',
  AGRI: 'Agrar',
}

//: Dieselbe Zuordnung als Auswahl. Der Wert ist der Speicherwert, das Label
//: das deutsche Wort — die Suche im Auswahlfeld greift auf beides zu.
const KATEGORIE_OPTIONEN: MultiSelectOption[] = [
  { value: 'ENERGY', label: 'Energie', hint: 'Öl, Gas' },
  { value: 'METAL', label: 'Metall', hint: 'Gold, Kupfer' },
  { value: 'AGRI', label: 'Agrar', hint: 'Weizen, Mais' },
]

//: Auswahl und Adresse: In der Adresse steht eine kommagetrennte Liste
//: (?category=ENERGY,METAL), die Komponente arbeitet mit einem Array.
function alsListe(wert: string): string[] {
  return wert ? wert.split(',').filter(Boolean) : []
}

const COLUMNS: Column<Commodity>[] = [
  { key: 'name', label: 'Name', sortable: true, minWidth: '180px' },
  // Der Code ist kopierbar wie die ISIN am Instrument: Er ist der Wert, den
  // man von hier aus weitergibt — in eine Abfrage, ein Ticket, eine Nachricht.
  { key: 'code', label: 'Code', sortable: true, mono: true, copy: true,
    minWidth: '130px',
    help: 'Unser eigener Schlüssel. Für Rohstoffe gibt es keine börsenübergreifende Kennung wie die ISIN bei Wertpapieren — die Börsen führen je eigene Ticker, die Datenanbieter noch einmal andere. Der Code hier bleibt stabil, auch wenn sich ein Ticker ändert.' },
  { key: 'category', label: 'Kategorie', sortable: true, minWidth: '120px',
    help: 'Grobe Einteilung in Energie, Metall und Agrar. Keine amtliche Taxonomie wie GICS bei den Aktien, sondern unsere eigene — sie gruppiert die Liste und mehr nicht.',
    render: (row) => CATEGORIES[row.category] ?? row.category },
  { key: 'unit', label: 'Einheit', sortable: true, minWidth: '110px',
    help: 'Die Menge, auf die sich ein Preis bezieht. Sie gehört zum Kontrakt und nicht zum Rohstoff: Kupfer notiert an der COMEX je Pfund und an der LME je Tonne — dasselbe Metall, zwei Einheiten. Hier steht die des ersten Tickers.' },
  { key: 'currency', label: 'Währung', sortable: true, mono: true,
    minWidth: '100px',
    help: 'Die Währung, in der wir den Preis führen wollen — nicht zwangsläufig die, in der die Börse notiert. Getreide etwa notiert an der CBOT in US-Cent je Bushel, nicht in Dollar.' },
  // Nicht sortierbar: Ein Array hat keine sinnvolle Reihenfolge, und nach
  // dem ersten Eintrag zu sortieren wäre eine Ordnung, die niemand erwartet.
  { key: 'tickers', label: 'Ticker', mono: true, minWidth: '230px',
    help: 'Zwei Sorten in einer Spalte, am Präfix zu unterscheiden: Vier Buchstaben sind der MIC der Börse, an der der Kontrakt läuft (IFEU:B = Brent an der ICE Futures Europe). Ein Anbietername steht für das Symbol, unter dem wir den Kurs holen (YAHOO:BZ=F). Das zweite ist kein Börsensymbol — das hat der Anbieter sich ausgedacht. Bei Aktien fallen beide zusammen, bei Rohstoffen nie.',
    render: (row) => row.tickers.length ? row.tickers.join(' · ') : '—' },
]

export function CommoditiesPage() {
  const allowed = useHasPermission(PERMISSION)

  // Der Zustand steht in der Adresse, nicht in der Komponente: teilbar,
  // überlebt das Neuladen, und die Zurück-Taste nimmt den letzten Schritt
  // zurück statt die Seite zu verlassen.
  const list = useListParams({
    filters: { category: '' },
    sort: 'name',
  })

  const params: CommodityQuery = {
    category: alsListe(list.filters.category),
    sort: list.sort as CommodityQuery['sort'],
    dir: list.descending ? 'desc' : 'asc',
    page: list.page,
    size: PAGE_SIZE,
  }

  // Nicht abfragen, wenn das Recht ohnehin fehlt: Der Aufruf liefe garantiert
  // ins 403.
  const { result, loading, error, denied } = useListQuery(
    api.commodities, params, allowed)

  if (!allowed || denied) {
    return <PermissionNotice permission={PERMISSION}
                             subject="die Rohstoffe" />
  }

  return (
    <>
      {/*
        NUR der Kategoriefilter, kein Suchfeld: Die Tabelle hat elf Zeilen.
        Sie passen auf einen Bildschirm, und wer dort etwas sucht, sieht es.
      */}
      <FilterBar>
        <FilterFixed>
          <MultiSelect label="Kategorie" options={KATEGORIE_OPTIONEN}
                       selected={alsListe(list.filters.category)}
                       onChange={(werte) =>
                         list.setFilter('category', werte.join(','))}
                       placeholder="alle Kategorien"
                       searchPlaceholder="Energie, Metall …" />
        </FilterFixed>
        <FilterEnd>
          <Button onClick={list.reset} disabled={!list.touched}>
            Zurücksetzen
          </Button>
        </FilterEnd>
      </FilterBar>

      {error && <ErrorBox>{error}</ErrorBox>}

      <Card flush span={12}>
        {/*
          Ohne `onRowClick`: Es gibt keine Detailansicht, weil es nichts zu
          zeigen gäbe — alle sieben fachlichen Felder stehen schon hier. Bei
          den Instrumenten ist das anders, dort zeigt die Liste vier von
          siebzehn.
        */}
        <DataTable
          columns={COLUMNS}
          rows={result?.items ?? []}
          rowKey={(row) => row.id}
          sort={{ key: list.sort, descending: list.descending }}
          onSort={(next) => list.setSort(next.key, next.descending)}
          page={result ? { page: result.page, size: result.size,
                           total: result.total, hasMore: result.has_more }
                       : undefined}
          onPage={list.setPage}
          loading={loading && result === null}
          empty={<EmptyState title="keine Rohstoffe">
            {list.touched
              ? 'Kategoriefilter zurücksetzen.'
              : 'Es sind noch keine Rohstoffe eingetragen.'}
          </EmptyState>}
        />
      </Card>
    </>
  )
}
